import React from 'react';
import { Box, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import ConstructionPriceEstimator from '../stages/ConstructionPriceEstimator';
import { FlowContext } from '../components/StageWrapper';
import { createAppTheme } from '../theme';

const flowId = 'davis-bacon-schedule-e2e';
const storageKey = 'davis-bacon-schedule-e2e-job';
const reviewStorageKey = 'davis-bacon-schedule-e2e-review';

type FixtureJobStatus = 'running' | 'completed' | 'cancelled';
type FixtureReviewState = 'approved' | 'rejected';

const initialDraft = {
  draft_revision: 4,
  content_hash: 'fixture-accepted-draft-hash',
  project_location: { country: 'US', state: 'GA', county: 'Lowndes', city: 'Valdosta', postal_code: '31601' },
  estimate_date: '2026-09-03', construction_type: 'Roof replacement',
  davis_bacon_construction_type: 'Building',
  determination_reference_date: '2026-09-01', determination_reference_purpose: 'acquisition', davis_bacon_posture: 'wage_floor_research',
  size_drivers: [], work_items: [], project_assumptions: [], exclusions: [], adjustments: [], required_acknowledgements: [],
};

let draft = { ...initialDraft };

const savedStatus = (): FixtureJobStatus | null => {
  const value = window.localStorage.getItem(storageKey);
  return value === 'running' || value === 'completed' || value === 'cancelled' ? value : null;
};

const storeStatus = (value: FixtureJobStatus | null) => {
  if (value) window.localStorage.setItem(storageKey, value);
  else window.localStorage.removeItem(storageKey);
};

const savedReviewState = (): FixtureReviewState | null => {
  const value = window.localStorage.getItem(reviewStorageKey);
  return value === 'approved' || value === 'rejected' ? value : null;
};

const storeReviewState = (value: FixtureReviewState | null) => {
  if (value) window.localStorage.setItem(reviewStorageKey, value);
  else window.localStorage.removeItem(reviewStorageKey);
};

const scenarioName = () => new URLSearchParams(window.location.search).get('scenario');

const scheduleState = (scenario = scenarioName()) => {
  const review = scenario === 'review';
  const reviewState = review ? savedReviewState() : null;
  const reviewRequired = review && reviewState === null;
  const validated = scenario === 'validated';
  return {
    reviewRequired,
    status: reviewRequired ? 'review_required' : reviewState === 'rejected' ? 'rejected' : 'available',
    review_state: reviewRequired ? 'review_required' : reviewState ?? (validated ? 'validated' : 'approved'),
    ...(reviewRequired ? { review_token: 'review-fixture' } : {}),
  };
};

const response = (data: unknown, status = 200) => new Response(JSON.stringify({ success: true, data }), {
  status, headers: { 'Content-Type': 'application/json' },
});

const fixtureLoad = () => {
  const status = savedStatus();
  const scenario = scenarioName();
  const blocked = scenario === 'blocked';
  const stale = scenario === 'stale';
  const review = scenario === 'review';
  const viewer = scenario === 'viewer';
  // This is intentionally query-selected only for isolated browser acceptance:
  // it proves the backend-valid available/validated pair independently of the
  // fixture's ordinary approved completion and review/cancel paths.
  const validated = scenario === 'validated';
  const completed = status === 'completed' || review || stale || validated || viewer;
  const selectedScheduleState = scheduleState(scenario);
  return {
    capabilities: {
      applicable: true, enabled: true, calculation_available: true, export_available: false,
      path_policy_version: 2, can_edit: !viewer, can_calculate: !viewer,
      davis_bacon_schedule: blocked
        ? { available: false, reason: 'prerequisites_required' }
        : { available: true, reason: null, can_view_details: completed && !viewer, can_review: selectedScheduleState.reviewRequired && !viewer },
    },
    draft,
    // A completed synthetic ROM, not a priced labor estimate. Even approved
    // rates do not invent a supported recipe, craft mapping or person-hours.
    active_revision: completed ? {
      revision: 1, status: 'calculated', mode: 'historical_comparable_rom',
      currency: 'USD', estimate_date: draft.estimate_date,
      work_items: [], comparables: { items: [] }, warnings: [],
      approximate_cost_plan: {
        scope_label: 'Synthetic roof replacement', status_label: 'Not estimated',
        installed_quantity_label: 'Quantity not supplied', purchased_quantity_label: 'Not estimated',
        assumptions: ['Synthetic fixture only; no supported labor recipe or craft mapping.'],
        exclusions: [], labor_total: null, materials_total: null, priced_subtotal: null,
        labor_lines: [], material_lines: [], other_costs: [], fee: null,
        labor_coverage: {
          status: 'unavailable', missing_crafts: [],
          reason: selectedScheduleState.review_state === 'review_required'
            ? 'Wage Determination review is required; labor is not estimated.'
            : selectedScheduleState.review_state === 'rejected'
              ? 'The Wage Determination was rejected; labor is not estimated.'
              : 'No supported labor recipe or craft mapping is supplied; labor is not estimated.',
        },
        award_reference: null, unallocated_difference: null,
        comparison_label: 'No comparable award reference supplied.',
      },
    } : null,
    revision_history: [],
    job: status ? { job_id: 'fixture-wd-job', status, capabilities: { can_cancel: status === 'running' && !viewer } } : null,
    readiness: { ready_to_calculate: true, blockers: [] },
    davis_bacon_schedule: completed ? {
      determination_id: 'GA20260042', revision: '3', state: 'GA', county: 'Lowndes', construction_type: 'Building',
      reference_date: '2026-09-01', retrieved_at: '2026-09-03T00:00:00+00:00',
      ...selectedScheduleState, classification_count: 2, schedule_id: 'schedule-fixture',
    } : null,
  };
};

/** Browser-only isolated fixture. It never contacts a backend, SAM.gov, or a user flow. */
function installFixtureFetch() {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = typeof input === 'string' ? input : input.toString();
    const url = new URL(request, window.location.origin);
    const method = (init?.method ?? 'GET').toUpperCase();
    const base = `/api/construction-price-estimator/${flowId}`;
    const parseBody = () => typeof init?.body === 'string' ? JSON.parse(init.body) as Record<string, unknown> : {};
    if (url.pathname === base && method === 'GET') return response(fixtureLoad());
    if (scenarioName() === 'viewer' && (method !== 'GET' || url.pathname === `${base}/davis-bacon-schedule/details`)) {
      return new Response(JSON.stringify({ success: false, error: 'Fixture viewer access is summary-only.' }), { status: 403 });
    }
    if (url.pathname === `${base}/draft` && method === 'PUT') {
      const body = parseBody();
      draft = { ...draft, ...body, draft_revision: draft.draft_revision + 1, content_hash: `fixture-accepted-${draft.draft_revision + 1}` };
      return response(draft);
    }
    if (url.pathname === `${base}/research` && method === 'POST') {
      const body = parseBody();
      const allowed = ['expected_draft_revision', 'expected_draft_content_hash', 'force_refresh'];
      if (Object.keys(body).length !== allowed.length || Object.keys(body).some((key) => !allowed.includes(key))
        || body.expected_draft_revision !== draft.draft_revision || body.expected_draft_content_hash !== draft.content_hash
        || typeof body.force_refresh !== 'boolean') return new Response(JSON.stringify({ success: false, error: 'Unexpected fixture request.' }), { status: 400 });
      storeStatus('running');
      return response({ job_id: 'fixture-wd-job', status: 'running', capabilities: { can_cancel: true } }, 202);
    }
    if (url.pathname === `${base}/calculations/fixture-wd-job` && method === 'GET') {
      const status = savedStatus();
      if (status === 'running') {
        // `running` is an acceptance selector: it never self-completes, so a
        // DevTools run has a deterministic window to exercise Cancel.
        if (scenarioName() === 'running') return response({ job_id: 'fixture-wd-job', status: 'running', capabilities: { can_cancel: true } });
        storeStatus('completed');
        return response({ job_id: 'fixture-wd-job', status: 'completed' });
      }
      return response({ job_id: 'fixture-wd-job', status: status ?? 'completed', capabilities: { can_cancel: false } });
    }
    if (url.pathname === `${base}/calculations/fixture-wd-job/cancel` && method === 'POST') {
      storeStatus('cancelled');
      return response({ job_id: 'fixture-wd-job', status: 'cancelled', capabilities: { can_cancel: false } });
    }
    if (url.pathname === `${base}/davis-bacon-schedule/details` && method === 'GET') {
      if (scenarioName() === 'viewer') return new Response(JSON.stringify({ success: false, error: 'Fixture viewer access is summary-only.' }), { status: 403, headers: { 'Content-Type': 'application/json' } });
      return response({ schedule_id: 'schedule-fixture', determination_id: 'GA20260042', revision: '3', state: 'GA', county: 'Lowndes', construction_type: 'Building', reference_date: '2026-09-01', retrieved_at: '2026-09-03T00:00:00+00:00', ...scheduleState(), classification_count: 2, rows: [{ classification: 'ELECTRICIAN', group: 'electric', base_wage: '31.25', fringe: '12.50', total_hourly: '43.75', unit: 'hour', footnote_identifiers: ['FOOTNOTE 1'] }] });
    }
    if (url.pathname === `${base}/davis-bacon-schedule/review` && method === 'POST') {
      const body = parseBody();
      if (Object.keys(body).join(',') !== 'schedule_id,expected_review_token,decision,reason') return new Response(JSON.stringify({ success: false, error: 'Unexpected fixture request.' }), { status: 400 });
      if (body.decision !== 'approve' && body.decision !== 'reject') return new Response(JSON.stringify({ success: false, error: 'Unexpected fixture request.' }), { status: 400 });
      if (!scheduleState().reviewRequired || body.schedule_id !== 'schedule-fixture' || body.expected_review_token !== 'review-fixture'
        || typeof body.reason !== 'string' || !body.reason.trim()) return new Response(JSON.stringify({ success: false, error: 'Unexpected fixture request.' }), { status: 400 });
      storeReviewState(body.decision === 'approve' ? 'approved' : 'rejected');
      return response({ schedule_id: 'schedule-fixture', determination_id: 'GA20260042', revision: '3', state: 'GA', county: 'Lowndes', construction_type: 'Building', reference_date: '2026-09-01', retrieved_at: '2026-09-03T00:00:00+00:00', ...scheduleState(), classification_count: 2 });
    }
    return new Response(JSON.stringify({ success: false, error: 'Unexpected fixture request.' }), { status: 404, headers: { 'Content-Type': 'application/json' } });
  };
}

export default function DavisBaconScheduleFixture() {
  installFixtureFetch();
  return (
    <ThemeProvider theme={createAppTheme()}>
      <CssBaseline />
      <FlowContext.Provider value={{ flowName: 'Wage determination fixture', canGoBack: false, standaloneMode: true, isViewOnly: scenarioName() === 'viewer' }}>
        <Box component="main" data-testid="davis-bacon-schedule-e2e-shell" sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 3 }}>
          <Typography variant="overline" color="primary">Browser test fixture</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Synthetic local data only. No user profile, workflow, SAM.gov, or production service is used.</Typography>
          <ConstructionPriceEstimator flowId={flowId} pollIntervalMs={250} />
        </Box>
      </FlowContext.Provider>
    </ThemeProvider>
  );
}
